// Chat Sessions Service
// Handles chat session management - separate from the main Sidekick API

import CONFIG from './configService'
import type { Session, Message } from '../types'


export interface UpdateSessionRequest {
    title?: string
    lastMessage?: string
    messageCount?: number
}

export class SessionsService {
    private baseUrl: string

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl.replace(/\/$/, '') // Remove trailing slash
    }

    /**
     * Get user sessions from the backend API
     */
    async getSessions(userId?: string, token?: string): Promise<Session[]> {

        const response = await fetch(`${this.baseUrl}/sessions`, {
            headers: {
                ...(token && { 'Authorization': `Bearer ${token}` }),
            },
        })

        if (!response.ok) {
            throw new Error(`Failed to fetch sessions: ${response.status}`)
        }

        const data = await response.json()

        // Map the API response to our ChatSession interface
        const sessions: Session[] = (data.sessions || []).map((session: any) => ({
            id: session.sessionId,
            title: this.generateTitleLocally(session.message || ''),
            lastMessage: session.message,
            createdAt: session.createdAt,
            updatedAt: session.createdAt, // Use createdAt as updatedAt if not provided
            messageCount: 1, // API doesn't provide this, default to 1
            userId: userId
        }))

        return sessions
    }

    /**
     * Get a specific session by ID
     */
    async getSession(sessionId: string, token?: string): Promise<Session | null> {
        const response = await fetch(`${this.baseUrl}/sessions/${sessionId}`, {
            headers: {
                ...(token && { 'Authorization': `Bearer ${token}` }),
            },
        })

        if (response.status === 404) {
            return null
        }

        if (!response.ok) {
            throw new Error(`Failed to fetch session: ${response.status}`)
        }

        const data = await response.json()

        // Handle the actual API response format
        if (data.sessionId && data.events) {
            // Convert events to Message[]
            const { eventToMessage } = await import('./eventToMessage');
            let messages = data.events.map((event: any) => eventToMessage(event));
            // Sort by timestamp (createdAt or timestamp) ascending
            messages = messages.sort((a: Message, b: Message) => {
                const ta = a.timestamp ?? (a.createdAt ? new Date(a.createdAt).getTime() : 0);
                const tb = b.timestamp ?? (b.createdAt ? new Date(b.createdAt).getTime() : 0);
                return ta - tb;
            });

            // Find the most recent user message to use as title
            const userMessages = messages.filter((msg: any) => msg.role === 'user');
            const lastUserMessage = userMessages[userMessages.length - 1];

            // Find the most recent assistant message
            const assistantMessages = messages.filter((msg: any) => msg.role === 'assistant');
            const lastAssistantMessage = assistantMessages[assistantMessages.length - 1];

            return {
                id: data.sessionId,
                title: this.generateTitleLocally(lastUserMessage?.content || 'Conversation'),
                lastMessage: lastAssistantMessage?.content,
                createdAt: messages[messages.length - 1]?.createdAt || new Date().toISOString(),
                updatedAt: messages[0]?.createdAt || new Date().toISOString(), // Most recent is first
                messageCount: messages.length,
                messages
            }
        }

        // Fallback to original format if structure is different
        return data.session || data
    }

    /**
     * Create a new chat session
     */
    async createSession(_token?: string): Promise<Session> {


        // const response = await fetch(`${this.baseUrl}/sessions`, {
        //     method: 'POST',
        //     headers: {
        //         'Content-Type': 'application/json',
        //         ...(token && { 'Authorization': `Bearer ${token}` }),
        //     },
        //     body: JSON.stringify(request),
        // })

        // if (!response.ok) {
        //     throw new Error(`Failed to create session: ${response.status}`)
        // }

        // const data = await response.json()
        // return data.session || data

        return {
            id: crypto.randomUUID(),
            title: '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            messageCount: 0
        }
    }

    /**
     * Update a session (title, metadata, etc.)
     */
    async updateSession(sessionId: string, request: UpdateSessionRequest, token?: string): Promise<Session> {
        const response = await fetch(`${this.baseUrl}/sessions/${sessionId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` }),
            },
            body: JSON.stringify(request),
        })

        if (!response.ok) {
            throw new Error(`Failed to update session: ${response.status}`)
        }

        const data = await response.json()
        return data.session || data
    }

    /**
     * Delete a session
     */
    async deleteSession(sessionId: string, token?: string): Promise<void> {
        const response = await fetch(`${this.baseUrl}/sessions/${sessionId}`, {
            method: 'DELETE',
            headers: {
                ...(token && { 'Authorization': `Bearer ${token}` }),
            },
        })

        if (!response.ok) {
            throw new Error(`Failed to delete session: ${response.status}`)
        }
    }

    /**
     * Send feedback for a session
     */
    async sendFeedback(sessionId: string, score: number, token?: string): Promise<Response> {
        const response = await fetch(`${this.baseUrl}/sessions/${sessionId}/feedback`, {
            method: 'PUT',
            headers: {
                ...(token && { 'Authorization': `Bearer ${token}` }),
            },
            body: JSON.stringify({
                score: score,
            }),
        });

        if (!response.ok) {
            throw new Error(`Failed to send feedback: ${response.statusText}`);
        }

        return response;
    }

    /**
     * Generate a title for a session based on the first message
     */
    async generateSessionTitle(sessionId: string, firstMessage: string, token?: string): Promise<string> {
        const response = await fetch(`${this.baseUrl}/api/sessions/${sessionId}/generate-title`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` }),
            },
            body: JSON.stringify({ message: firstMessage }),
        })

        if (!response.ok) {
            // Fallback to client-side title generation if API doesn't support it
            return this.generateTitleLocally(firstMessage)
        }

        const data = await response.json()
        return data.title || this.generateTitleLocally(firstMessage)
    }

    /**
     * Client-side title generation fallback
     */
    private generateTitleLocally(message: string): string {
        // Remove common prefixes
        let title = message.replace(/^(can you|could you|please|help me|how do i|how to|what is|what are)/i, '').trim()

        // Capitalize first letter
        title = title.charAt(0).toUpperCase() + title.slice(1)

        // Limit length
        const maxLength = 50
        if (title.length > maxLength) {
            title = title.substring(0, maxLength).trim()
            // Try to end at a word boundary
            const lastSpace = title.lastIndexOf(' ')
            if (lastSpace > maxLength * 0.7) {
                title = title.substring(0, lastSpace)
            }
            title += '...'
        }

        return title || 'New conversation'
    }
}

// Default instance - use real service
export const sessionsService = new SessionsService(CONFIG.BACKEND_API_URL)